import React, { useEffect, useState } from 'react';
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import { v4 as uuid } from 'uuid';
import { cloneDeep } from 'lodash';
import SimpleDialog from '../../../../features/common/SimpleDialog';
import FallbackSpinner from '../../../../materio/@core/components/spinner';
import SectionAppBar from '../../../components/Section/SectionAppBar';
import Button from '../../../components/Button/StandardButton';
import useApplication from '../../../hooks/useApplication.hooks';
import useAwaitableComponent from '../../../../hooks/useAwaitableComponent';
import ApplicationStore from '../../../../stores/ApplicationStore';
import { client } from '../../../../utils/axios-client';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CloseIcon from '@mui/icons-material/Close';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SaveButton from '../../../components/Button/SaveButton';
import ConfirmationDialog from '../../../../features/common/ConfirmationDialog';
import { StageLeadSession } from './types/types';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DataGrid } from '@mui/x-data-grid/DataGrid';
import PencilOutline from 'mdi-material-ui/PencilOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { PagedList } from '../../../../types/common';
import { formatDateLocal, timeSince } from '../../../../utils/date-utils';
import AccountImport from '../../Accounts/AccountImport/AccountImport';
import { Account } from '../../../../stores/AccountStore';
import useAccountImport from '../../Accounts/AccountImport/hooks/AccountImport.hooks';

interface CellType {
    row: StageLeadSession;
}

type Props = {
    onOpenImportDialog: () => void;
    onOpenSession: (row: StageLeadSession) => void;
    openResearch: () => void;
};

const LeadImportList = ({ onOpenImportDialog, onOpenSession, openResearch }: Props) => {
    const [data, setData] = useState<PagedList<StageLeadSession>>({
        items: [],
        pageNumber: 1,
        pageSize: 10,
        hasNextPage: false,
        totalCount: 0,
        totalPages: 0,
    });

    const { openAccountImport } = useAccountImport();
    const { isLoading, setIsLoading } = useApplication();
    const [recordsLoaded, setRecordsLoaded] = useState(false);
    const [openSubOutcomes, setOpenSubOutcomes] = useState(false);
    const [selectedSession, setSelectedSession] = useState<null | StageLeadSession>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [status, execute, resolve, reject, reset] = useAwaitableComponent();
    const showModal = status !== 'idle';
    const [pageSize, setPageSize] = useState(10);
    const [value, setValue] = useState<string>('');
    const [editOpen, setEditOpen] = useState<boolean>(false);

    useEffect(() => {
        loadRecords(0, pageSize);
    }, []);

    const loadRecords = async (pageNumber: number, pageSize: number) => {
        setIsLoading(true);
        try {
            const response = await client.get(`/api/leadresearch/list`, {
                params: { pageNumber: pageNumber + 1, pageSize },
            });
            setData(response.data);
        } finally {
            setRecordsLoaded(true);
            setIsLoading(false);
        }
    };

    const handleConfirmDelete = async (row: StageLeadSession) => {
        try {
            await execute();
            const temp = cloneDeep(data);
            const rowIndex = temp.items.findIndex((s) => s.id === row.id);
            if (rowIndex > -1) {
                temp.items[rowIndex].isDeleted = true;
                setData(temp);
                setIsDirty(true);
            }
            ApplicationStore.update((s) => {
                s.snackbarMessage = 'Session successfully deleted';
                s.snackbarOpen = true;
            });
        } catch (err) {
        } finally {
            reset();
        }
    };

    const onRowClick = (row: StageLeadSession) => {
        onOpenSession(row);
        //setSelectedSession(row);
        //setEditOpen(true);
    };

    const columns = [
        {
            flex: 0.2,
            minWidth: 230,
            field: 'name',
            headerName: 'Name',
            renderCell: ({ row }: CellType) => {
                const { id, name } = row;

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component="a"
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {name}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            minWidth: 230,
            field: 'playbookCadenceName',
            headerName: 'Cadence Name',
            renderCell: ({ row }: CellType) => {
                const { id, playbookCadenceName } = row;

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component="a"
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {playbookCadenceName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            field: 'sprintName',
            headerName: 'Sprint',
            renderCell: ({ row }: CellType) => {
                const { id, sprintName } = row;

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component="a"
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {sprintName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            field: 'customerName',
            headerName: 'Customer',
            renderCell: ({ row }: CellType) => {
                const { id, customerName } = row;

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component="a"
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {customerName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            field: 'lastModified',
            headerName: 'Last Modified',
            renderCell: ({ row }: CellType) => {
                const { id, lastModified } = row;

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component="a"
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {timeSince(lastModified)}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.1,
            field: '',
            maxWidth: 150,
            headerName: 'Actions',
            renderCell: ({ row }: CellType) => {
                return (
                    <>
                        <IconButton onClick={() => onRowClick(row)} title="Edit">
                            <PencilOutline fontSize="small" />
                        </IconButton>
                    </>
                );
            },
        },
    ];

    if (!recordsLoaded)
        return (
            <Box>
                <Box sx={{ position: 'sticky', top: 0, width: '100%' }}>
                    <FallbackSpinner />
                </Box>
            </Box>
        );

    if (!data) return <Box>Error fetching outcome types</Box>;

    console.log('data', data);

    return (
        <Box>
            <SectionAppBar
                title="Lead Import History"
                subTitle={''}
                buttons={
                    <>
                        <Button color="secondary" onClick={onOpenImportDialog} icon={<UploadFileIcon />}>
                            Import Leads
                        </Button>
                        <Button color="secondary" onClick={openAccountImport} icon={<UploadFileIcon />}>
                            Import Accounts
                        </Button>
                        <Button color="secondary" onClick={openResearch} icon={<PersonSearchIcon />}>
                            Lead Research
                        </Button>
                    </>
                }
            />
            <Box>
                <DataGrid
                    paginationMode="server"
                    rowCount={data.totalCount}
                    autoHeight
                    pagination
                    rows={data.items}
                    columns={columns}
                    pageSize={data.pageSize}
                    disableSelectionOnClick
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageSizeChange={(newPageSize: number) => loadRecords(0, newPageSize)}
                    onPageChange={(pageNumber: number) => loadRecords(pageNumber, data.pageSize)}
                />
                <Divider sx={{ mt: 4, mb: 4 }} />
            </Box>

            <AccountImport
                onContinue={(accounts: Account[]) => {
                    console.log(accounts);
                }}
            />
        </Box>
    );
};

export default LeadImportList;
